import classNames from "classnames";
import React, { AriaAttributes, FC, ReactNode, useCallback, useMemo } from "react";
import { Button, ButtonProps } from "../button";
import { Collapse } from "../collapse/collapse";
import { CollapseProps } from "../collapse/collapse.types";
import { IconNavArrowDown, IconNavArrowUp } from "../icons";
import { Separator } from "../separator/separator";
import { ElementRenderer } from "../types";
import { CSS_CLASSES } from "./accordion-css-classes";

export interface AccordionSectionInternalProps
  extends Pick<CollapseProps, "preserveDomElement" | "lazyInitialCollapse">,
    Pick<ButtonProps, "ripple"> {
  index?: number;
  onAccordionSectionClick?: (event: React.MouseEvent<HTMLElement>, index: number, active: boolean) => void;
  active?: boolean;
  separatorClassName?: string;
  hideSeparator?: boolean;
  color?: "interactive" | "primary" | "white";
}

export interface AccordionSectionProps {
  /**
   * The HTML id attribute to be applied to the root element of this component.
   * If not passed, a unique `id` will be generated and applied when used in conjunction with the `Accordion` component.
   */
  id?: string;

  /**
   * Apply/override styling by passing in custom CSS class-name(s) using this prop.
   */
  className?: string;

  /**
   * The text label displayed on the accordion section header.
   */
  label: string;

  /**
   * The aria-level value in which the component will he rendered.
   *
   * @default 6
   */
  headerAriaLevel?: number;

  /**
   * This props can be used to disable a particular accordion section.
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * This props controls if the accordion section is collapsible or not. If `false` the section always shows expanded.
   *
   * @default true
   */
  collapsible?: boolean;

  /**
   * A custom render function that allows to take full control of how the accordion section header is rendered.
   */
  headerRenderer?: AccordionSectionHeaderRenderer;

  /**
   * Content of the accordion section. Must either be text, number or a single, valid React Element.
   */
  children?: ReactNode;
}

export type AccordionSectionHeaderRenderer = ElementRenderer<AccordionSectionHeaderRendererProps>;

export type AccordionSectionHeaderRendererProps = Pick<
  AccordionSectionProps,
  "id" | "label" | "collapsible" | "disabled"
> &
  Pick<AccordionSectionInternalProps, "index" | "active" | "onAccordionSectionClick" | "color"> & {
    ariaProps: Pick<AriaAttributes, "aria-expanded" | "aria-controls">;
  };

export const AccordionSection: FC<AccordionSectionProps & AccordionSectionInternalProps> = ({
  label,
  headerAriaLevel = 6,
  active,
  separatorClassName,
  hideSeparator,
  color = "interactive",
  onAccordionSectionClick,
  ripple,
  id,
  index,
  children,
  disabled,
  collapsible = true,
  headerRenderer,
  preserveDomElement,
  lazyInitialCollapse,
  className,
  ...restProps
}) => {
  active = !collapsible ? true : active;
  const startIcon = collapsible ? active ? <IconNavArrowUp /> : <IconNavArrowDown /> : null;

  const handleSectionClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => onAccordionSectionClick(e, index, active),
    [active, index, onAccordionSectionClick]
  );

  const headerId = useMemo(() => `${id}-toggle-button`, [id]);

  return (
    <div {...restProps}>
      <div
        role="heading"
        aria-level={headerAriaLevel}
        className={classNames(CSS_CLASSES.ACCORDION_SECTION_HEADING, className)}
      >
        {headerRenderer && typeof headerRenderer === "function" ? (
          headerRenderer({
            id: headerId,
            index,
            active,
            label,
            onAccordionSectionClick,
            color,
            collapsible,
            disabled,
            ariaProps: { "aria-expanded": active, "aria-controls": id },
          })
        ) : (
          <Button
            ripple={ripple}
            onClick={handleSectionClick}
            color={color}
            disabled={disabled}
            size="medium"
            shape="square"
            aria-expanded={active}
            aria-controls={id}
            className={CSS_CLASSES.ACCORDION_SECTION_HEADING_BUTTON}
            label={label}
            width="full"
            labelAlign="left"
            startIcon={startIcon}
            variant="ghost"
            id={headerId}
            styleOverride={!collapsible ? "normal" : null}
          />
        )}
      </div>
      <div id={id} role="region" aria-labelledby={headerId}>
        {children && (
          <Collapse expanded={active} preserveDomElement={preserveDomElement} lazyInitialCollapse={lazyInitialCollapse}>
            {children}
          </Collapse>
        )}
      </div>
      {!hideSeparator && <Separator gap="none" orientation="horizontal" className={separatorClassName} />}
    </div>
  );
};
